import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Wallet, Landmark, ArrowDownCircle, ArrowUpCircle, Eye, BarChart3, CreditCard, Calculator, Printer } from "lucide-react";
import { printAccountStatement } from "@/lib/printAccountStatement";
import { printAccountTxn } from "@/lib/printAccountTxn";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name: "", kind: "cash", opening_balance: 0, currency: "QAR", is_default: false, notes: "" };

const TXN_PAGE_SIZE = 100;

const buildTxnQuery = ({ start, end, limit = TXN_PAGE_SIZE, offset = 0 }) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  return params.toString();
};

const fetchTxnPage = async (accountId, opts) => {
  const r = await api.get(`/accounts/${accountId}/transactions?${buildTxnQuery(opts)}`);
  return r.data;
};

const fetchAllTxnsInRange = async (accountId, start, end) => {
  const out = [];
  let offset = 0;
  const limit = 500;
  while (true) {
    const page = await fetchTxnPage(accountId, { start, end, limit, offset });
    out.push(...(page.items || []));
    if (!page.has_more) break;
    offset += limit;
  }
  return out;
};

export default function Accounts() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [txnOpen, setTxnOpen] = useState(null); // account_id — view modal
  const [txns, setTxns] = useState([]);
  // Separate "add new transaction" modal (independent from view modal).
  const [newTxnFor, setNewTxnFor] = useState(null); // account_id
  const [txnForm, setTxnForm] = useState({ kind: "deposit", amount: 0, description: "" });
  const [txnSaving, setTxnSaving] = useState(false);

  // Wave C — new operational state
  const [summary, setSummary] = useState({ total: 0, by_kind: {}, account_count: 0 });
  const [methods, setMethods] = useState([]);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodForm, setMethodForm] = useState({ name: "", type: "cash", target_account_id: "", is_active: true, is_default: false, sort_order: 0 });
  const [methodEditId, setMethodEditId] = useState(null);
  const [eodOpen, setEodOpen] = useState(false);
  const [eodForm, setEodForm] = useState({ account_id: "", counted_amount: 0, notes: "" });
  // Print template + statement period picker
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtPreset, setStmtPreset] = useState("all");
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const monthStartISO = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const monthEndISO = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
  const yearStartISO = (d = new Date()) => new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
  const yearEndISO = (d = new Date()) => new Date(d.getFullYear(), 11, 31).toISOString().slice(0, 10);
  const [stmtRange, setStmtRange] = useState({ start: monthStartISO(), end: monthEndISO() });
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [txnMeta, setTxnMeta] = useState({ total: 0, limit: TXN_PAGE_SIZE, offset: 0, has_more: false });
  const [txnFilterPreset, setTxnFilterPreset] = useState("month");
  const [txnRange, setTxnRange] = useState({ start: monthStartISO(), end: monthEndISO() });
  const [txnFilterMonth, setTxnFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [txnLoading, setTxnLoading] = useState(false);

  // Load print template (logo, business name, tax number) for branded statements.
  useEffect(() => {
    api.get("/invoice-template")
      .then((r) => setInvoiceTemplate(r.data || null))
      .catch(() => setInvoiceTemplate(null));
  }, []);

  const load = async () => {
    const [r, s, m] = await Promise.all([
      api.get("/accounts"),
      api.get("/accounts/summary").catch(() => ({ data: { total: 0, by_kind: {}, account_count: 0 } })),
      api.get("/payment-methods").catch(() => ({ data: [] })),
    ]);
    setAccounts(r.data);
    setSummary(s.data);
    setMethods(m.data);
  };
  useEffect(() => { load(); }, []);

  // Wave C — payment method CRUD
  const submitMethod = async () => {
    if (!methodForm.name) { toast.error(t("required_field")); return; }
    try {
      // Backend expects target_account_id either as a real id or null. The
      // empty-string from the <select> sentinel option must be normalised so
      // the server doesn't try to look up an account by "".
      const payload = { ...methodForm, target_account_id: methodForm.target_account_id || null };
      if (methodEditId) await api.put(`/payment-methods/${methodEditId}`, payload);
      else await api.post("/payment-methods", payload);
      toast.success(t("saved"));
      setMethodModalOpen(false);
      setMethodForm({ name: "", type: "cash", target_account_id: "", is_active: true, is_default: false, sort_order: 0 });
      setMethodEditId(null);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };
  const deleteMethod = async (id) => {
    const ok = await confirm({
      title: "Delete payment method?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/payment-methods/${id}`); load(); }
    catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  // Wave C — End-of-Day cash count
  const submitEod = async () => {
    if (!eodForm.account_id) { toast.error(t("required_field")); return; }
    try {
      const r = await api.post(`/accounts/${eodForm.account_id}/cash-count`, {
        counted_amount: parseFloat(eodForm.counted_amount) || 0,
        notes: eodForm.notes,
      });
      const v = r.data.variance;
      toast.success(v === 0
        ? (lang === "ar" ? "تطابق ✓" : "Match ✓")
        : (lang === "ar" ? `فرق: ${v > 0 ? "+" : ""}${v}` : `Variance: ${v > 0 ? "+" : ""}${v}`));
      setEodOpen(false);
      setEodForm({ account_id: "", counted_amount: 0, notes: "" });
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const submit = async () => {
    const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
    if (editId) await api.put(`/accounts/${editId}`, payload);
    else await api.post("/accounts", payload);
    toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/accounts/${id}`); load();
  };

  const applyTxnFilterPreset = (p) => {
    setTxnFilterPreset(p);
    const now = new Date();
    if (p === "all") {
      setTxnRange({ start: "", end: "" });
    } else if (p === "month") {
      setTxnRange({ start: monthStartISO(), end: monthEndISO() });
    } else if (p === "prev_month") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      setTxnRange({ start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) });
    } else if (p === "year") {
      setTxnRange({ start: yearStartISO(), end: yearEndISO() });
    } else if (p === "ytd") {
      setTxnRange({ start: yearStartISO(), end: todayISO() });
    } else if (p === "specific_month") {
      const [y, m] = txnFilterMonth.split("-").map(Number);
      const ref = new Date(y, m - 1, 1);
      setTxnRange({ start: monthStartISO(ref), end: monthEndISO(ref) });
    }
  };

  useEffect(() => {
    if (txnFilterPreset !== "specific_month") return;
    const [y, m] = txnFilterMonth.split("-").map(Number);
    if (!y || !m) return;
    const ref = new Date(y, m - 1, 1);
    setTxnRange({ start: monthStartISO(ref), end: monthEndISO(ref) });
  }, [txnFilterMonth, txnFilterPreset]);

  const loadTxns = async (accountId, { reset = true, offset: offsetOverride } = {}) => {
    if (!accountId) return;
    setTxnLoading(true);
    try {
      const offset = reset ? 0 : (offsetOverride ?? txns.length);
      const page = await fetchTxnPage(accountId, {
        start: txnRange.start || undefined,
        end: txnRange.end || undefined,
        limit: TXN_PAGE_SIZE,
        offset,
      });
      setTxnMeta({
        total: page.total || 0,
        limit: page.limit || TXN_PAGE_SIZE,
        offset: page.offset || 0,
        has_more: !!page.has_more,
      });
      setTxns((prev) => (reset ? (page.items || []) : [...prev, ...(page.items || [])]));
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      setTxnLoading(false);
    }
  };

  const openTxns = async (accountId) => {
    const range = { start: monthStartISO(), end: monthEndISO() };
    setTxnFilterPreset("month");
    setTxnRange(range);
    setTxnOpen(accountId);
    setTxnLoading(true);
    try {
      const page = await fetchTxnPage(accountId, { start: range.start, end: range.end, limit: TXN_PAGE_SIZE, offset: 0 });
      setTxnMeta({
        total: page.total || 0,
        limit: page.limit || TXN_PAGE_SIZE,
        offset: page.offset || 0,
        has_more: !!page.has_more,
      });
      setTxns(page.items || []);
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      setTxnLoading(false);
    }
  };

  // Print full statement (all transactions) for an account card directly.
  const printAllForAccount = async (account) => {
    try {
      const items = await fetchAllTxnsInRange(account.account_id);
      printAccountStatement({
        account,
        transactions: items,
        period: null,
        template: invoiceTemplate,
        lang: undefined,
      });
    } catch {
      toast.error(t("print_failed"));
    }
  };

  // Apply preset to statement date range (used inside modal).
  const applyStmtPreset = (p) => {
    setStmtPreset(p);
    const now = new Date();
    if (p === "month") setStmtRange({ start: monthStartISO(), end: monthEndISO() });
    else if (p === "prev_month") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      setStmtRange({ start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) });
    } else if (p === "year") setStmtRange({ start: yearStartISO(), end: yearEndISO() });
    else if (p === "ytd") setStmtRange({ start: yearStartISO(), end: todayISO() });
    else if (p === "specific_month") {
      const [y, m] = stmtMonth.split("-").map(Number);
      const ref = new Date(y, m - 1, 1);
      setStmtRange({ start: monthStartISO(ref), end: monthEndISO(ref) });
    }
  };

  useEffect(() => {
    if (stmtPreset !== "specific_month") return;
    const [y, m] = stmtMonth.split("-").map(Number);
    if (!y || !m) return;
    const ref = new Date(y, m - 1, 1);
    setStmtRange({ start: monthStartISO(ref), end: monthEndISO(ref) });
  }, [stmtMonth, stmtPreset]);

  // Print statement with the modal's selected period (or all).
  const printPeriodStatement = async () => {
    const acct = accounts.find((a) => a.account_id === txnOpen);
    if (!acct) return;
    try {
      const items = stmtPreset === "all"
        ? await fetchAllTxnsInRange(acct.account_id)
        : await fetchAllTxnsInRange(acct.account_id, stmtRange.start, stmtRange.end);
      printAccountStatement({
        account: acct,
        transactions: items,
        period: stmtPreset === "all" ? null : { start: stmtRange.start, end: stmtRange.end },
        template: invoiceTemplate,
        lang: undefined,
      });
      setStmtOpen(false);
    } catch {
      toast.error(t("print_failed"));
    }
  };

  const submitTxn = async () => {
    if (!parseFloat(txnForm.amount)) { toast.error(t("required_field")); return; }
    if (!newTxnFor) { toast.error(t("account_not_selected")); return; }
    setTxnSaving(true);
    try {
      const acct = accounts.find((a) => a.account_id === newTxnFor);
      const r = await api.post(`/accounts/${newTxnFor}/txn`, {
        account_id: newTxnFor,
        kind: txnForm.kind,
        amount: parseFloat(txnForm.amount),
        description: txnForm.description,
      });
      toast.success(lang === "ar" ? "تم الحفظ ✓" : "Saved");
      // Backend returns { ok, balance, txn }. Use the real txn if present
      // so the voucher prints the canonical id + signed amount + balance.
      const serverTxn = r?.data?.txn;
      const signedAmount = txnForm.kind === "deposit"
        ? Math.abs(parseFloat(txnForm.amount))
        : -Math.abs(parseFloat(txnForm.amount));
      const printableTxn = serverTxn || {
        txn_id: `T-${Date.now()}`,
        amount: signedAmount,
        balance_after: (r?.data?.balance != null)
          ? r.data.balance
          : (Number(acct?.balance || 0) + signedAmount),
        description: txnForm.description,
        created_at: new Date().toISOString(),
      };
      printAccountTxn({ txn: printableTxn, account: acct, template: invoiceTemplate, lang });
      setNewTxnFor(null);
      setTxnForm({ kind: "deposit", amount: 0, description: "" });
      // If view modal is open on same account, refresh current filter.
      if (txnOpen === newTxnFor) {
        await loadTxns(newTxnFor, { reset: true });
      }
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      setTxnSaving(false);
    }
  };

  const total = accounts.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-4xl gold-text">{t("nav_accounts")}</h1>
        <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-account-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16}/>{t("add")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {accounts.map(a => (
          <div key={a.account_id} className="card-surface p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md border border-[var(--border)] flex items-center justify-center">
                  {a.kind === "bank" ? <Landmark size={20} className="text-[var(--gold)]"/> : <Wallet size={20} className="text-[var(--gold)]"/>}
                </div>
                <div>
                  <div className="font-heading text-lg text-[var(--text)]">{a.name}</div>
                  <div className="text-xs text-[var(--text-muted)]">{t(a.kind === "bank" ? "bank_account" : "cash_account")} {a.is_default && <span className="badge badge-gold ms-2">{t("default_account")}</span>}</div>
                </div>
              </div>
            </div>
            <div className="gold-text font-heading text-3xl num-display mb-4">{fmt(a.balance)}</div>
            <div className="flex gap-2">
              <button onClick={() => openTxns(a.account_id)} data-testid={`view-txns-${a.account_id}`} className="btn-soft flex-1 px-3 py-2 rounded-md text-sm flex items-center justify-center gap-2"><Eye size={14}/>{t("view_transactions")}</button>
              <button onClick={() => { setTxnForm({ kind: "deposit", amount: 0, description: "" }); setNewTxnFor(a.account_id); }} data-testid={`new-txn-${a.account_id}`} title={lang === "ar" ? "إضافة معاملة" : "New transaction"} className="p-2 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)]"><Plus size={14}/></button>
              <button onClick={() => printAllForAccount(a)} data-testid={`print-stmt-${a.account_id}`} title={lang === "ar" ? "طباعة كشف" : "Print statement"} className="p-2 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)]"><Printer size={14}/></button>
              <button onClick={() => { setForm(a); setEditId(a.account_id); setOpen(true); }} className="p-2 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
              <button onClick={() => remove(a.account_id)} className="p-2 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        {accounts.length === 0 && <div className="col-span-full text-center text-[var(--text-muted)] py-12">{t("no_data")}</div>}
      </div>

      {accounts.length > 0 && (
        <div className="card-surface p-5 flex items-center justify-between">
          <div className="text-[var(--text-muted)]">{t("total")}</div>
          <div className="gold-text font-heading text-3xl num-display">{fmt(total)}</div>
        </div>
      )}

      {/* Wave C — Cash Position widget */}
      <div className="card-surface p-5" data-testid="cash-position-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-[var(--gold)]"/>
            <h2 className="font-heading text-xl text-[var(--text)]">{t("cash_position") || "الوضع النقدي"}</h2>
          </div>
          <button onClick={() => { setEodForm({ account_id: summary.default_account_id || (accounts[0]?.account_id || ""), counted_amount: 0, notes: "" }); setEodOpen(true); }}
            data-testid="open-eod-btn"
            className="btn-soft px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Calculator size={14}/>{t("eod_cash_count") || "جرد آخر اليوم"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[var(--border-subtle)] rounded-md p-3">
            <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1">{t("total") || "الإجمالي"}</div>
            <div className="gold-text font-heading text-2xl num-display" data-testid="cash-total">{fmt(summary.total)}</div>
          </div>
          <div className="border border-[var(--border-subtle)] rounded-md p-3">
            <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1 flex items-center gap-1"><Wallet size={11}/>{t("cash_account") || "نقد"}</div>
            <div className="text-[var(--text)] font-heading text-xl num-display">{fmt(summary.by_kind?.cash || 0)}</div>
          </div>
          <div className="border border-[var(--border-subtle)] rounded-md p-3">
            <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1 flex items-center gap-1"><Landmark size={11}/>{t("bank_account") || "بنك"}</div>
            <div className="text-[var(--text)] font-heading text-xl num-display">{fmt(summary.by_kind?.bank || 0)}</div>
          </div>
          <div className="border border-[var(--border-subtle)] rounded-md p-3">
            <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-1">{t("accounts") || "الحسابات"}</div>
            <div className="text-[var(--text)] font-heading text-xl num-display">{summary.account_count || accounts.length}</div>
          </div>
        </div>
      </div>

      {/* Wave C — Payment Methods card */}
      <div className="card-surface p-5" data-testid="payment-methods-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-[var(--gold)]"/>
            <h2 className="font-heading text-xl text-[var(--text)]">{t("payment_methods") || "طرق الدفع"}</h2>
          </div>
          <button onClick={() => { setMethodEditId(null); setMethodForm({ name: "", type: "cash", target_account_id: "", is_active: true, is_default: false, sort_order: 0 }); setMethodModalOpen(true); }}
            data-testid="add-method-btn"
            className="btn-gold px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Plus size={14}/>{t("add")}
          </button>
        </div>
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr>
              <th>{t("name") || "الاسم"}</th>
              <th>{t("type") || "النوع"}</th>
              <th>{t("default_account") || "افتراضي"}</th>
              <th>{t("status") || "الحالة"}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {methods.map(m => (
                <tr key={m.method_id} data-testid={`method-row-${m.method_id}`}>
                  <td className="text-[var(--text)]">{m.name}</td>
                  <td><span className="badge">{t(m.type) || m.type}</span></td>
                  <td>{m.is_default ? <span className="badge badge-gold">{t("default_account") || "افتراضي"}</span> : "-"}</td>
                  <td>{m.is_active ? <span className="badge badge-green">{t("active") || "نشط"}</span> : <span className="badge badge-rose">{t("inactive") || "موقوف"}</span>}</td>
                  <td className="text-end">
                    <button onClick={() => { setMethodEditId(m.method_id); setMethodForm({ name: m.name, type: m.type, target_account_id: m.target_account_id || "", is_active: m.is_active, is_default: m.is_default, sort_order: m.sort_order || 0 }); setMethodModalOpen(true); }}
                      className="p-2 rounded border border-[var(--border)] hover:border-[var(--gold)] me-1"><Pencil size={14}/></button>
                    <button onClick={() => deleteMethod(m.method_id)} data-testid={`del-method-${m.method_id}`}
                      className="p-2 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
              {methods.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("account_name")}</label><input className="input-dark" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="acc-name"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("account_kind")}</label>
                <select className="input-dark" value={form.kind} onChange={e => setForm({...form, kind: e.target.value})}>
                  <option value="cash">{t("cash_account")}</option><option value="bank">{t("bank_account")}</option>
                </select>
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("opening_balance")}</label><input type="number" className="input-dark num-display" value={form.opening_balance} onChange={e => setForm({...form, opening_balance: e.target.value})} disabled={!!editId}/></div>
              <div className="flex items-center gap-2 pt-5 md:col-span-2">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm({...form, is_default: e.target.checked})}/>
                <span className="text-sm text-[var(--text)]">{t("default_account")}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="acc-save" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {txnOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setTxnOpen(null))}>
          <div className="card-surface p-6 w-full max-w-3xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-2xl gold-text">{t("transactions")}</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => { setTxnForm({ kind: "deposit", amount: 0, description: "" }); setNewTxnFor(txnOpen); }}
                  data-testid="view-modal-add-txn"
                  className="btn-gold px-3 py-2 rounded-md text-sm flex items-center gap-2">
                  <Plus size={14}/>{lang === "ar" ? "معاملة جديدة" : "New transaction"}
                </button>
                <button onClick={() => { setStmtPreset("all"); setStmtOpen(true); }} data-testid="open-stmt-picker"
                  className="px-3 py-2 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center gap-2">
                  <Printer size={14}/>{lang === "ar" ? "طباعة كشف الحساب" : "Print statement"}
                </button>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "ar"
                ? "اختر الفترة ثم اضغط تطبيق. يمكن تحميل المزيد عند وجود حركات إضافية."
                : "Pick a date range and apply. Use Load more when additional entries exist."}
            </p>
            <div className="space-y-3 border border-[var(--border-subtle)] rounded-md p-3 bg-[var(--bg)]/40">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { v: "month", l: t("period_this_month") },
                  { v: "prev_month", l: t("period_prev_month") },
                  { v: "ytd", l: t("period_ytd") },
                  { v: "all", l: t("period_all") },
                  { v: "specific_month", l: t("period_specific_month") },
                  { v: "custom", l: t("period_custom") },
                ].map((p) => (
                  <button
                    key={p.v}
                    type="button"
                    onClick={() => applyTxnFilterPreset(p.v)}
                    data-testid={`txn-filter-${p.v}`}
                    className={`px-2 py-1.5 rounded-md text-xs border text-center ${txnFilterPreset === p.v ? "btn-gold" : "border-[var(--border)] hover:border-[var(--gold)]"}`}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
              {txnFilterPreset === "specific_month" && (
                <input type="month" value={txnFilterMonth} onChange={(e) => setTxnFilterMonth(e.target.value)} className="input-dark" data-testid="txn-filter-month" />
              )}
              {txnFilterPreset !== "all" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-[var(--text-muted)] block">{t("range_from")}</label>
                    <input type="date" value={txnRange.start} onChange={(e) => { setTxnRange({ ...txnRange, start: e.target.value }); setTxnFilterPreset("custom"); }}
                      className="input-dark text-sm" data-testid="txn-filter-from" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[var(--text-muted)] block">{t("range_to")}</label>
                    <input type="date" value={txnRange.end} onChange={(e) => { setTxnRange({ ...txnRange, end: e.target.value }); setTxnFilterPreset("custom"); }}
                      className="input-dark text-sm" data-testid="txn-filter-to" />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs text-[var(--text-muted)] num-display" data-testid="txn-filter-summary">
                  {txnMeta.total > 0
                    ? (lang === "ar"
                      ? `عرض ${txns.length} من ${txnMeta.total}`
                      : `Showing ${txns.length} of ${txnMeta.total}`)
                    : (lang === "ar" ? "لا حركات في هذه الفترة" : "No transactions in this period")}
                </span>
                <button type="button" onClick={() => loadTxns(txnOpen, { reset: true })} disabled={txnLoading}
                  className="btn-soft px-3 py-1.5 rounded text-xs" data-testid="txn-filter-apply">
                  {lang === "ar" ? "تطبيق" : "Apply"}
                </button>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="table-dark">
                <thead><tr><th>{t("date")}</th><th>{t("status")}</th><th>{t("amount")}</th><th>{t("current_balance")}</th><th>{t("description")}</th></tr></thead>
                <tbody>
                  {txns.map(tx => (
                    <tr key={tx.txn_id}>
                      <td className="text-[var(--text-muted)] num-display">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td>{tx.amount > 0 ? <span className="badge badge-green flex items-center gap-1 w-fit"><ArrowDownCircle size={12}/>{t("deposit")}</span> : <span className="badge badge-rose flex items-center gap-1 w-fit"><ArrowUpCircle size={12}/>{t("withdraw")}</span>}</td>
                      <td className={`num-display font-medium ${tx.amount > 0 ? "gold-text" : "rose-text"}`}>{tx.amount > 0 ? "+" : ""}{fmt(tx.amount)}</td>
                      <td className="num-display text-[var(--text)]">{fmt(tx.balance_after)}</td>
                      <td className="text-[var(--text-muted)]">{tx.description}</td>
                    </tr>
                  ))}
                  {txns.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--text-muted)] py-6">{t("no_data")}</td></tr>}
                </tbody>
              </table>
            </div>
            {txnMeta.has_more && (
              <div className="flex justify-center pt-2">
                <button type="button" onClick={() => loadTxns(txnOpen, { reset: false, offset: txns.length })} disabled={txnLoading}
                  className="btn-soft px-4 py-2 rounded-md text-sm" data-testid="txn-load-more">
                  {txnLoading ? (lang === "ar" ? "جارٍ التحميل..." : "Loading...") : (lang === "ar" ? "تحميل المزيد" : "Load more")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Standalone "New Transaction" modal — opened from the account card or from inside the view modal. */}
      {newTxnFor && (() => {
        const acct = accounts.find((a) => a.account_id === newTxnFor);
        const amountNum = parseFloat(txnForm.amount) || 0;
        const isWithdraw = txnForm.kind === "withdraw";
        const balanceAfter = (acct?.balance || 0) + (isWithdraw ? -amountNum : amountNum);
        const willOverdraw = isWithdraw && balanceAfter < 0;
        return (
        <div className="fixed inset-0 z-[60] bg-[#2B1C1C]/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => { if (!txnSaving) setNewTxnFor(null); })}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="new-txn-modal">
            <div className="flex items-center gap-2">
              {isWithdraw
                ? <ArrowUpCircle size={20} className="text-rose-500"/>
                : <ArrowDownCircle size={20} className="text-emerald-500"/>}
              <h2 className="font-heading text-2xl gold-text">
                {lang === "ar" ? "معاملة جديدة" : "New Transaction"}
              </h2>
            </div>

            {acct && (
              <div className="border border-[var(--border-subtle)] rounded-md p-3 bg-[var(--gold-tint)] text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {acct.kind === "bank"
                      ? <Landmark size={14} className="text-[var(--gold)]"/>
                      : <Wallet size={14} className="text-[var(--gold)]"/>}
                    <span className="font-medium text-[var(--text)]">{acct.name}</span>
                    <span className="text-xs text-[var(--text-muted)]">· {acct.kind === "bank" ? (lang === "ar" ? "بنك" : "Bank") : (lang === "ar" ? "نقد" : "Cash")}</span>
                  </div>
                  <div className="num-display gold-text font-bold">{fmt(acct.balance)}</div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? "نوع الحركة" : "Type"}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setTxnForm({ ...txnForm, kind: "deposit" })} data-testid="txn-kind-deposit"
                    className={`px-3 py-2 rounded-md text-sm border flex items-center justify-center gap-2 ${txnForm.kind === "deposit" ? "border-emerald-500 bg-emerald-500/10 text-emerald-700" : "border-[var(--border)] text-[var(--text)]"}`}>
                    <ArrowDownCircle size={14}/>{lang === "ar" ? "إيداع" : "Deposit"}
                  </button>
                  <button onClick={() => setTxnForm({ ...txnForm, kind: "withdraw" })} data-testid="txn-kind-withdraw"
                    className={`px-3 py-2 rounded-md text-sm border flex items-center justify-center gap-2 ${txnForm.kind === "withdraw" ? "border-rose-500 bg-rose-500/10 text-rose-700" : "border-[var(--border)] text-[var(--text)]"}`}>
                    <ArrowUpCircle size={14}/>{lang === "ar" ? "سحب" : "Withdraw"}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? "المبلغ" : "Amount"}</label>
                <input type="number" min="0" step="0.01" className="input-dark num-display text-xl"
                  value={txnForm.amount} onChange={e => setTxnForm({ ...txnForm, amount: e.target.value })}
                  data-testid="new-txn-amount"/>
              </div>

              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? "البيان / السبب" : "Description / Purpose"}</label>
                <input className="input-dark" value={txnForm.description}
                  onChange={e => setTxnForm({ ...txnForm, description: e.target.value })}
                  placeholder={lang === "ar" ? "مثال: استلام إيراد، صرف نثريات..." : "e.g. revenue collection, petty cash..."}
                  data-testid="new-txn-desc"/>
              </div>

              {acct && amountNum > 0 && (
                <div className={`border rounded-md p-3 text-sm ${willOverdraw ? "border-rose-400 bg-rose-50" : "border-[var(--border-subtle)] bg-[var(--surface)]"}`}>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">{lang === "ar" ? "الرصيد الحالي" : "Current balance"}</span>
                    <span className="num-display">{fmt(acct.balance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">{lang === "ar" ? "بعد الحركة" : "After this entry"}</span>
                    <span className={`num-display font-bold ${willOverdraw ? "text-rose-600" : "gold-text"}`}>{fmt(balanceAfter)}</span>
                  </div>
                  {willOverdraw && (
                    <div className="mt-2 text-xs text-rose-600">
                      {lang === "ar" ? "تنبيه: ستجعل الرصيد بالسالب." : "Warning: this will make the balance negative."}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-sm" disabled={txnSaving} onClick={() => setNewTxnFor(null)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-50"
                disabled={txnSaving || !amountNum} onClick={submitTxn} data-testid="new-txn-save">
                <Printer size={14}/>
                {txnSaving
                  ? (lang === "ar" ? "جارٍ الحفظ..." : "Saving...")
                  : (lang === "ar" ? "حفظ وطباعة السند" : "Save & Print voucher")}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Wave C — EOD Cash Count modal */}
      {eodOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setEodOpen(false))}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="eod-modal">
            <h2 className="font-heading text-2xl gold-text flex items-center gap-2"><Calculator size={20}/>{t("eod_cash_count") || "جرد آخر اليوم"}</h2>
            <p className="text-sm text-[var(--text-muted)]">{t("eod_help") || "اكتب المبلغ المعدود فعلياً. سيتم احتساب الفرق تلقائياً ضد رصيد الدفتر، وأي فرق سيُسجَّل كقيد تسوية واضح."}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("account") || "الحساب"}</label>
                <select className="input-dark" value={eodForm.account_id} onChange={e => setEodForm({ ...eodForm, account_id: e.target.value })} data-testid="eod-account">
                  <option value="">--</option>
                  {accounts.filter(a => a.kind === "cash").map(a => (
                    <option key={a.account_id} value={a.account_id}>{a.name} ({fmt(a.balance)})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("counted_amount") || "المبلغ المعدود"}</label>
                <input type="number" step="0.01" className="input-dark num-display" value={eodForm.counted_amount}
                  onChange={e => setEodForm({ ...eodForm, counted_amount: e.target.value })} data-testid="eod-amount"/>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("notes") || "ملاحظات"}</label>
                <input className="input-dark" value={eodForm.notes} onChange={e => setEodForm({ ...eodForm, notes: e.target.value })} data-testid="eod-notes"/>
              </div>
              {eodForm.account_id && (() => {
                const acc = accounts.find(a => a.account_id === eodForm.account_id);
                const exp = acc ? parseFloat(acc.balance || 0) : 0;
                const cnt = parseFloat(eodForm.counted_amount) || 0;
                const variance = +(cnt - exp).toFixed(2);
                return (
                  <div className="border border-[var(--border-subtle)] rounded-md p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("expected") || "المتوقع"}</span><span className="num-display">{fmt(exp)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("counted") || "المعدود"}</span><span className="num-display">{fmt(cnt)}</span></div>
                    <div className="flex justify-between font-medium"><span className="text-[var(--text)]">{t("variance") || "الفرق"}</span>
                      <span className={`num-display ${variance === 0 ? "text-[var(--text)]" : variance > 0 ? "gold-text" : "rose-text"}`} data-testid="eod-variance">{variance > 0 ? "+" : ""}{fmt(variance)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setEodOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="eod-save" onClick={submitEod}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Wave C — Payment Method modal */}
      {methodModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setMethodModalOpen(false))}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="method-modal">
            <h2 className="font-heading text-2xl gold-text">{methodEditId ? t("edit") : t("add")} · {t("payment_methods") || "طرق الدفع"}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("name") || "الاسم"}</label>
                <input className="input-dark" value={methodForm.name} onChange={e => setMethodForm({ ...methodForm, name: e.target.value })} data-testid="method-name"/>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("type") || "النوع"}</label>
                <select className="input-dark" value={methodForm.type} onChange={e => setMethodForm({ ...methodForm, type: e.target.value })} data-testid="method-type">
                  <option value="cash">{t("cash") || "نقد"}</option>
                  <option value="card">{t("card") || "بطاقة"}</option>
                  <option value="bank">{t("bank") || "تحويل بنكي"}</option>
                  <option value="wallet">{t("wallet") || "محفظة"}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">
                  {t("linked_account")}
                </label>
                <select
                  className="input-dark"
                  value={methodForm.target_account_id || ""}
                  onChange={e => setMethodForm({ ...methodForm, target_account_id: e.target.value })}
                  data-testid="method-target-account"
                >
                  <option value="">{t("no_link_manual")}</option>
                  {accounts.map(a => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.name} ({a.kind === "cash" ? (lang === "ar" ? "صندوق" : "Cash") : (lang === "ar" ? "بنك" : "Bank")})
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-[var(--text-muted)] mt-1">
                  {t("method_target_help")}
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("sort_order") || "الترتيب"}</label>
                <input type="number" className="input-dark num-display" value={methodForm.sort_order}
                  onChange={e => setMethodForm({ ...methodForm, sort_order: parseInt(e.target.value || 0) })}/>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={methodForm.is_active} onChange={e => setMethodForm({ ...methodForm, is_active: e.target.checked })}/>
                {t("active") || "نشط"}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={methodForm.is_default} onChange={e => setMethodForm({ ...methodForm, is_default: e.target.checked })} data-testid="method-default"/>
                {t("default_pos_auto")}
              </label>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setMethodModalOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="method-save" onClick={submitMethod}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Statement period picker — choose period then print branded A4 statement */}
      {stmtOpen && (
        <div className="fixed inset-0 z-[60] bg-[#2B1C1C]/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setStmtOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="stmt-picker-modal">
            <div className="flex items-center gap-2">
              <Printer size={20} className="text-[var(--gold)]"/>
              <h2 className="font-heading text-2xl gold-text">{t("select_statement_period")}</h2>
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              {t("statement_period_help")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { v: "all", l: t("period_all") },
                { v: "month", l: t("period_this_month") },
                { v: "prev_month", l: t("period_prev_month") },
                { v: "ytd", l: t("period_ytd") },
                { v: "year", l: t("period_year") },
                { v: "specific_month", l: t("period_specific_month") },
                { v: "custom", l: t("period_custom") },
              ].map(p => (
                <button key={p.v} onClick={() => applyStmtPreset(p.v)} data-testid={`stmt-preset-${p.v}`}
                  className={`px-3 py-2 rounded-md text-xs border text-center ${stmtPreset === p.v ? "btn-gold" : "border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:border-[var(--gold)]"}`}>
                  {p.l}
                </button>
              ))}
            </div>
            {stmtPreset === "specific_month" && (
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{t("select_month")}</label>
                <input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} className="input-dark" data-testid="stmt-month-picker"/>
              </div>
            )}
            {stmtPreset !== "all" && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--border-subtle)]">
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] block">{t("range_from")}</label>
                  <input type="date" value={stmtRange.start} onChange={(e) => { setStmtRange({ ...stmtRange, start: e.target.value }); setStmtPreset("custom"); }}
                    className="input-dark text-sm" data-testid="stmt-from"/>
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] block">{t("range_to")}</label>
                  <input type="date" value={stmtRange.end} onChange={(e) => { setStmtRange({ ...stmtRange, end: e.target.value }); setStmtPreset("custom"); }}
                    className="input-dark text-sm" data-testid="stmt-to"/>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-end pt-2">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-sm" onClick={() => setStmtOpen(false)}>{t("cancel")}</button>
              <button onClick={printPeriodStatement} data-testid="stmt-print-confirm"
                className="btn-gold px-5 py-2 rounded-md text-sm flex items-center gap-2">
                <Printer size={14}/>{t("print")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
